import React from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { TaskContentAnswerEntity } from '../../../types';
import { GetTaskContentById } from '../../../utils/game-document/assets/task-contents';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { fileName } from '../../../utils/file-utility';
import { GameDocumentContext } from '../../../contexts/game-document';
import { GetTaskContentByTaskId } from '../../../utils/game-engine/player';

const AnswerColumn: React.FC<GridCellProps> = ({ dataItem, field }) => {
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [data, setData] = React.useState<React.ReactNode>(<></>);

  const generateUrl = async (value: string) => {
    const urlObject = new URL(value);
    const pathname = urlObject.pathname;
    const parts = pathname.split('/');
    let blobName = decodeURIComponent(parts.pop()!) || null;
    let result = value;

    if (blobName) {
      const response = await fetch(value);
      const blob = await response.blob();
      blobName = await fileName(blobName, blob.type, blob);
      result = window.URL.createObjectURL(blob);
    }

    return { href: result, download: blobName };
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  React.useEffect(() => {
    const getAnswerList = async (
      taskContentId: string,
      formId: string,
      answers: TaskContentAnswerEntity
    ) => {
      if (!taskContentId) return;

      let taskContent = GetTaskContentById(
        gameDocument?.gameDocument,
        taskContentId!
      );

      let form = taskContent?.forms?.find((x) => x.id === formId);

      if (form) {
        if (form.type === 'checkbox' || form.type === 'radio') {
          let answerDetail: React.ReactNode[] = [];

          answers?.answers
            ?.filter((x) => x.value)
            ?.forEach((answer, index) => {
              const description = GetResourceValue(
                gameDocument?.gameDocument!,
                answer.answerResId,
                ''
              );
              answerDetail.push(
                <li key={index} className={'text-break'}>
                  {description}
                </li>
              );
            });

          setData(<ul>{answerDetail}</ul>);
        } else if (
          form.type === 'file' ||
          form.type === 'video' ||
          form.type === 'image'
        ) {
          answers?.answers?.forEach(async (answer, index) => {
            const { href, download } = await generateUrl(answer.value);

            setData(
              <a
                href={href}
                download={download}
                target="_blank"
                className={'text-primary'}>
                Open
              </a>
            );
          });
        } else {
          answers?.answers?.forEach((answer) => {
            setData(
              <label className={'text-break'}>{answer?.value ?? '-'}</label>
            );
          });
        }
      }
    };

    const getARAnswerList = async (
      type: 'Task' | 'OnDemandTask',
      taskId: string,
      taskContentId: string,
      answer: Record<string, any>
    ) => {
      const taskContent =
        type === 'Task'
          ? GetTaskContentByTaskId(gameDocument.gameDocument!, taskId)
          : GetTaskContentById(gameDocument.gameDocument, taskContentId);

      if (taskContent) {
        const ARType = taskContent.ARMiniGame;

        switch (ARType) {
          case 'Game Cube':
            setData(
              <div className={'d-flex flex-column'}>
                {answer.passed ? (
                  <span className={'text-success'}>Passed</span>
                ) : (
                  <span className={'text-danger'}>Fail</span>
                )}
                <span>Duration: {formatDuration(answer.duration)}</span>
              </div>
            );
            break;
          case 'Meerkats Moves':
            const { href, download } = await generateUrl(answer.videoUrl);

            setData(
              <a
                href={href}
                download={download}
                target="_blank"
                className={'text-primary'}>
                Open
              </a>
            );
            break;
          case 'Sound Waves':
            setData(
              <span>Answer rate: {answer.correctAnswerRate / 20}/5</span>
            );
            break;
          case 'Summer Suitcase':
            setData(
              <div className={'d-flex flex-column'}>
                {answer.passed ? (
                  <span className={'text-success'}>Passed</span>
                ) : (
                  <span className={'text-danger'}>Fail</span>
                )}
                <span>Duration: {formatDuration(answer.duration)}</span>
              </div>
            );
            break;
          case 'The Portal':
            const { href: hrefPortal, download: downloadPortal } =
              await generateUrl(answer.photoUrl);

            setData(
              <div className={'d-flex flex-column'}>
                {answer.passed ? (
                  <span className={'text-success'}>Passed</span>
                ) : (
                  <span className={'text-danger'}>Fail</span>
                )}
                <a
                  href={hrefPortal}
                  download={downloadPortal}
                  target="_blank"
                  className={'text-primary'}>
                  Open
                </a>
              </div>
            );
            break;
          case 'Where in the World?':
          case 'Where in the World? - Easy':
          case 'Where in the World? - Hard':
            setData(
              <div className={'d-flex flex-column'}>
                <span>Answer rate: {answer.correctAnwerRate / 20}/5</span>
                <span>Duration: {formatDuration(answer.duration)}</span>
              </div>
            );
            break;
        }
      }
    };

    if (dataItem && dataItem.taskContentId)
      getAnswerList(dataItem.taskContentId, dataItem.formId, dataItem.answers);

    // indicate column is AR task
    if (dataItem && dataItem.taskId === dataItem.formId)
      getARAnswerList(
        dataItem.taskType,
        dataItem.taskId,
        dataItem.taskContentId,
        dataItem.answers
      );
  }, [dataItem, field, gameDocument]);

  return <td>{data}</td>;
};

export default AnswerColumn;
